import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  HeartIcon as HeartSolidIcon,
  StarIcon,
  EyeIcon,
  ShoppingCartIcon,
  TrashIcon,
} from '@heroicons/react/24/solid';
import { HeartIcon } from '@heroicons/react/24/outline';

const FavoritesPage = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sample favorites data - Bu normalde localStorage'dan veya API'den gelecek
  useEffect(() => {
    const sampleFavorites = [
      {
        id: 1,
        name: 'Milano Executive Desk',
        price: 24999,
        salePrice: 19999,
        image: 'https://images.unsplash.com/photo-1631679706909-1844bbd07221?w=600&h=400&fit=crop&crop=center',
        category: 'Office',
        rating: 4.9,
        reviews: 127,
        addedDate: '2024-01-15'
      },
      {
        id: 2,
        name: 'Venetian Luxury Sofa',
        price: 45999,
        salePrice: 39999,
        image: 'https://images.unsplash.com/photo-1567538096630-e0c55bd6374c?w=600&h=400&fit=crop&crop=center',
        category: 'Living Room',
        rating: 4.8,
        reviews: 89,
        addedDate: '2024-01-10'
      },
      {
        id: 4,
        name: 'Emerald Armchair',
        price: 18999,
        salePrice: 15999,
        image: 'https://images.unsplash.com/photo-1586773860418-d37222d8fce3?w=600&h=400&fit=crop&crop=center',
        category: 'Living Room',
        rating: 4.7,
        reviews: 203,
        addedDate: '2024-01-08'
      },
    ];

    // Simulate loading
    setTimeout(() => {
      setFavorites(sampleFavorites);
      setLoading(false);
    }, 1000);
  }, []);

  const removeFavorite = (productId) => {
    setFavorites(favorites.filter(item => item.id !== productId));
  };

  const addToCart = (product) => {
    // Sepete ekleme işlemi
    console.log('Sepete eklendi:', product);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Favorileriniz yükleniyor...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-rose-100 rounded-full mb-6">
            <HeartSolidIcon className="h-8 w-8 text-rose-600" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-luxury font-bold text-gray-900 mb-4">
            Favorilerim
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Beğendiğiniz ürünleri kaydettiniz ve kolayca erişebilirsiniz
          </p>
          <div className="mt-4 text-sm text-gray-500">
            {favorites.length} ürün listeleniyor
          </div>
        </motion.div>

        {/* Favorites Grid */}
        {favorites.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center py-16"
          >
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gray-100 rounded-full mb-6">
              <HeartIcon className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">
              Henüz favori ürününüz yok
            </h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Beğendiğiniz ürünleri favorilere ekleyerek kolayca erişebilirsiniz
            </p>
            <Link
              to="/products"
              className="inline-flex items-center px-6 py-3 bg-rose-600 text-white font-semibold rounded-lg hover:bg-rose-700 transition-colors duration-300"
            >
              Ürünleri Keşfet
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {favorites.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg overflow-hidden group hover:shadow-xl transition-all duration-300"
              >
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  
                  {/* Remove from favorites */}
                  <button
                    onClick={() => removeFavorite(product.id)}
                    className="absolute top-3 right-3 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center text-rose-600 hover:bg-rose-600 hover:text-white transition-all duration-300 shadow-lg"
                    title="Favorilerden Çıkar"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>

                  {/* Sale Badge */}
                  {product.salePrice && (
                    <div className="absolute top-3 left-3 bg-red-500 px-2.5 py-1.5 rounded-lg shadow-sm">
                      <span className="text-xs font-bold text-white">
                        %{Math.round((1 - product.salePrice / product.price) * 100)} İndirim
                      </span>
                    </div>
                  )}

                  {/* Rating */}
                  <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm px-2.5 py-1.5 rounded-lg shadow-sm">
                    <div className="flex items-center space-x-1">
                      <StarIcon className="h-3.5 w-3.5 text-amber-400" />
                      <span className="text-xs font-semibold text-gray-800">{product.rating}</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
                        {product.name}
                      </h3>
                      <p className="text-sm text-gray-500 mb-2">{product.category}</p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="flex items-center space-x-2 mb-4">
                    {product.salePrice ? (
                      <>
                        <span className="text-xl font-bold text-emerald-600">
                          ₺{product.salePrice.toLocaleString()}
                        </span>
                        <span className="text-sm text-gray-400 line-through">
                          ₺{product.price.toLocaleString()}
                        </span>
                      </>
                    ) : (
                      <span className="text-xl font-bold text-gray-900">
                        ₺{product.price.toLocaleString()}
                      </span>
                    )}
                  </div>

                  {/* Added date */}
                  <p className="text-xs text-gray-400 mb-4">
                    Favorilere eklenme: {new Date(product.addedDate).toLocaleDateString('tr-TR')}
                  </p>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <Link
                      to={`/products/${product.id}`}
                      className="flex-1 bg-slate-100 hover:bg-slate-700 hover:text-white text-slate-600 py-2.5 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 font-medium"
                    >
                      <EyeIcon className="h-4 w-4" />
                      <span>İncele</span>
                    </Link>
                    <button
                      onClick={() => addToCart(product)}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2 font-medium"
                    >
                      <ShoppingCartIcon className="h-4 w-4" />
                      <span>Sepete Ekle</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Bottom Actions */}
        {favorites.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-12 text-center"
          >
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-2xl mx-auto">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">
                Tüm Favorileri Yönet
              </h3>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => setFavorites([])}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition-colors duration-300"
                >
                  Tüm Favorileri Temizle
                </button>
                <Link
                  to="/products"
                  className="px-6 py-3 bg-rose-600 hover:bg-rose-700 text-white font-semibold rounded-lg transition-colors duration-300"
                >
                  Daha Fazla Ürün Keşfet
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage; 